import { FormEvent, useState, ReactNode, useEffect, useRef } from 'react';
import { Control, useController } from 'react-hook-form';
import { CountryCode } from 'libphonenumber-js/types';
import { useTranslation } from 'react-i18next';

import { getBusinessCountry } from 'pages/dashboard/selectors';
import { InfoCircleSolidIcon } from 'components/icons';
import PopoverOnHover from 'components/PopoverOnHover';
import { InputProps } from 'entities/inputs';
import { useAppSelector } from 'hooks/redux';
import {
  defaultFormatter,
  phoneFormatters,
  isValidPhoneNumber,
  getNationalDigits,
  PHONE_COUNTRIES,
} from 'utils/phone';

import ErrorMessage from './components/ErrorMessage';
import Countries from './Countries';
import { Input } from './Input';

interface PhoneInputProps extends Omit<InputProps, 'onChange'> {
  control?: Control;
  country?: string;
  iconEnd?: ReactNode;
  iconStart?: ReactNode;
  readOnly?: boolean;
  validation?: any;
  selectCountry?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  control,
  label,
  info,
  className,
  error,
  optional,
  country,
  iconEnd,
  iconStart,
  validation = {},
  selectCountry = false,
  readOnly,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState<string>(country || 'US');
  const [codeError, setCodeError] = useState<boolean>(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  const businessCountry = useAppSelector(getBusinessCountry);

  const { field } = useController({ name, control, rules: validation });
  const controlValue = field.value;
  const prevCodeRef = useRef<string>(code);

  const validatePhoneNumber = (value: string) => {
    const isValidPhone = isValidPhoneNumber(value, code as CountryCode);
    setCodeError(!isValidPhone);
  };

  useEffect(() => {
    const countryCode = country || businessCountry?.alpha2Code || 'US';

    if (code !== countryCode) {
      setCode(countryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, businessCountry]);

  useEffect(() => {
    if (!controlValue) {
      setDisplayValue('');
      return;
    }

    const formatter = phoneFormatters[code] ?? defaultFormatter;
    const { display, e164 } = formatter(getNationalDigits(controlValue));

    setDisplayValue(display);

    validatePhoneNumber(e164);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlValue, code]);

  // When the country changes, re-anchor an already-entered number to the
  // new country: keep the national digits, drop the previous calling code,
  // and rewrite the stored E.164 so it persists with the new code.
  useEffect(() => {
    if (prevCodeRef.current === code) {
      return;
    }

    prevCodeRef.current = code;

    if (!controlValue) {
      return;
    }

    const formatter = phoneFormatters[code] ?? defaultFormatter;
    const { e164 } = formatter(getNationalDigits(controlValue));

    if (e164 && e164 !== controlValue) {
      field.onChange(e164);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleChangeCountry = (e: FormEvent<HTMLSelectElement>) => {
    setCode(e.currentTarget.value as CountryCode);
  };

  const handleChange = (
    e: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    callback,
  ) => {
    const raw = e.currentTarget.value || '';

    if (raw === '') {
      setDisplayValue('');
      setCodeError(false);
      callback('');
      return;
    }

    const formatter = phoneFormatters[code] ?? defaultFormatter;
    const { display, e164 } = formatter(raw);

    setDisplayValue(display);

    validatePhoneNumber(e164);

    callback(e164 || '');
  };

  return (
    <div className={className}>
      {label && (
        <div className="flex justify-between items-center mb-1">
          <label className="text-sm leading-5 font-medium text-gray-700">
            {label}
          </label>
          {info && (
            <PopoverOnHover
              info={info}
              button={<InfoCircleSolidIcon className="text-gray-400" />}
            />
          )}
          {optional && (
            <span className="text-sm leading-5 font-medium text-gray-500">
              {t('common:labelOptional')}
            </span>
          )}
        </div>
      )}

      <div className="relative">
        {iconStart && (
          <div className="absolute pl-3 inset-y-0 left-0 flex items-center pointer-events-none">
            {iconStart}
          </div>
        )}
        {selectCountry && (
          <Countries
            handleChangeCountry={handleChangeCountry}
            code={code as CountryCode}
            countries={PHONE_COUNTRIES}
            disabled={readOnly}
          />
        )}

        <Input
          name={name}
          value={displayValue}
          onChange={(e) => handleChange(e, field.onChange)}
          iconStart={iconStart}
          iconEnd={iconEnd}
          placeholder={
            PHONE_COUNTRIES.find((c) => c.code === code)?.placeholder
          }
          readOnly={readOnly}
          error={error || codeError}
          {...otherProps}
        />
      </div>

      {!error && codeError && (
        <ErrorMessage error={{ message: t('common:validations.phone') }} />
      )}
    </div>
  );
};

export default PhoneInput;
